import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import 'survey-core/i18n/traditional-chinese'
import {
  convertQuestionMappings,
  convertVisibleIf,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
    21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39,
    40,
  ]
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 防止閃閃
  useEffect(() => {
    let observer = null
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })
    }, 100)

    return () => {
      clearTimeout(timeout)
      if (observer) observer.disconnect()
    }
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  const visibleIfConditionsInit = convertVisibleIf({
    // 2-6-3 排序題選了(11)其他 → 顯示 2-6-3-11 說明
    q16: '{q15} anyof [11]',
    // 3-1-1/3-1-2 國籍：選項2=(1-2)本國籍具新住民身份 → 顯示原國籍；選項4=(2)外國籍 → 顯示國籍
    q19: '{q18} = 2',
    q20: '{q18} = 4',
    q22: '{q21} = 2',
    q23: '{q21} = 4',
    // 3-4-1-1 父親工作狀況：1有工作 2無工作 3不清楚或已死亡
    q28: '{q27} = 1',
    q29: '{q27} = 1',
    q30: '{q27} = 1',
    q31: '{q27} = 1',
    q32: '({q27} = 1) or ({q27} = 2)',
    q33: '({q27} = 1) or ({q27} = 2)',
    q34: '{q27} = 2',
    // 3-4-1-2 母親工作狀況（同上鏡像）
    q36: '{q35} = 1',
    q37: '{q35} = 1',
    q38: '{q35} = 1',
    q39: '{q35} = 1',
    q40: '({q35} = 1) or ({q35} = 2)',
    q41: '({q35} = 1) or ({q35} = 2)',
    q42: '{q35} = 2',
    // 3-5-1-1 父親育嬰假：1有 2沒有 3不清楚；3-5-4-1 返回職場：1有 2沒有 3請假中
    q44: '{q43} = 1',
    q45: '{q43} = 1',
    q46: '{q43} = 1',
    q47: '({q43} = 1) and ({q46} = 1)',
    q48: '({q43} = 1) and ({q46} = 1)',
    q49: '({q43} = 1) and ({q46} = 2)',
    q50: '{q43} = 2',
    // 3-5-1-2 母親育嬰假（同上鏡像）
    q52: '{q51} = 1',
    q53: '{q51} = 1',
    q54: '{q51} = 1',
    q55: '({q51} = 1) and ({q54} = 1)',
    q56: '({q51} = 1) and ({q54} = 1)',
    q57: '({q51} = 1) and ({q54} = 2)',
    q58: '{q51} = 2',
    // 4-4 排序題選了(22)其他 → 顯示 4-4-22 說明
    q63: '{q62} anyof [22]',
    // 4-5 變更戶籍：JSON 選項1=「否」、選項2=「是(填空 o_type6)」→ 選「是」值為 "other"；選否只跳過 4-6
    q65: '{q64} = "other"',
    // 4-7-0/4-8-0/4-9-0 閘門：選項1=(0)未曾使用、選項2=(1)曾使用過 → 曾使用過才顯示經驗題
    q67: '{q66} = 2',
    q69: '{q68} = 2',
    q71: '{q70} = 2',
    // 4-10 主要照顧者四分流：1家人 2保母 3托嬰中心 4幼兒園
    q73: '{q72} = 1',
    q74: '({q72} = 1) and ({q73} = 1)',
    q75: '({q72} = 1) and ({q73} = 2)',
    q76: '({q72} = 1) and ({q73} = 2)',
    q77: '{q72} = 2',
    q78: '{q72} = 2',
    q79: '{q72} = 2',
    q80: '{q72} = 2',
    q81: '{q72} = 2',
    q82: '{q72} = 2',
    q83: '{q72} = 2',
    q84: '{q72} = 2',
    // 4-10-2-6 費用子題：父題選項1~6=(2)副食品/(3)延托/(4)獎金/(5-1)(5-2)(5-3)其他，選項7=沒有其他費用(互斥)
    q85: '({q72} = 2) and ({q84} anyof [1])',
    q86: '({q72} = 2) and ({q84} anyof [2])',
    q87: '({q72} = 2) and ({q84} anyof [3])',
    q88: '({q72} = 2) and ({q84} anyof [4])',
    q89: '({q72} = 2) and ({q84} anyof [5])',
    q90: '({q72} = 2) and ({q84} anyof [6])',
    q91: '{q72} = 3',
    q92: '{q72} = 3',
    q93: '{q72} = 3',
    q94: '{q72} = 3',
    q95: '{q72} = 3',
    q96: '{q72} = 3',
    q97: '{q72} = 3',
    // 4-10-3-5 費用子題：父題選項1~6=(2)註冊/(3)副食品/(4)延托/(5-1)(5-2)(5-3)其他
    q98: '({q72} = 3) and ({q97} anyof [1])',
    q99: '({q72} = 3) and ({q97} anyof [2])',
    q100: '({q72} = 3) and ({q97} anyof [3])',
    q101: '({q72} = 3) and ({q97} anyof [4])',
    q102: '({q72} = 3) and ({q97} anyof [5])',
    q103: '({q72} = 3) and ({q97} anyof [6])',
    q104: '{q72} = 3',
    q105: '{q72} = 4',
    q106: '{q72} = 4',
    q107: '{q72} = 4',
    q108: '{q72} = 4',
    q109: '{q72} = 4',
    q110: '{q72} = 4',
    q111: '{q72} = 4',
    // 4-10-4-5 費用子題：父題選項1~6=(2)交通/(3)課後延托/(4)才藝/(5-1)(5-2)(5-3)其他
    q112: '({q72} = 4) and ({q111} anyof [1])',
    q113: '({q72} = 4) and ({q111} anyof [2])',
    q114: '({q72} = 4) and ({q111} anyof [3])',
    q115: '({q72} = 4) and ({q111} anyof [4])',
    q116: '({q72} = 4) and ({q111} anyof [5])',
    q117: '({q72} = 4) and ({q111} anyof [6])',
    q118: '{q72} = 4',
    // 5-2 身心障礙證明選(2)無 → 跳過 5-3
    q125: '{q124} = 1',
    // 5-5：JSON 選項1=「每天都沒吃」、選項2=「有吃(填空 o_type6)」→ 有吃值為 "other"
    q128: '{q127} = "other"',
    // 2-1 父親(1)或母親(2)至少勾一個才顯示 5-9
    q131: '{q6} anyof [1, 2]',
    // 7-1-1 ~ 7-1-20：選(B1)=選項3 → 顯示 B1 題；選(B2)=選項4 → 顯示 B2 題
    q137: '{q136} = 3',
    q138: '{q136} = 4',
    q140: '{q139} = 3',
    q141: '{q139} = 4',
    q143: '{q142} = 3',
    q144: '{q142} = 4',
    q146: '{q145} = 3',
    q147: '{q145} = 4',
    q149: '{q148} = 3',
    q150: '{q148} = 4',
    q152: '{q151} = 3',
    q153: '{q151} = 4',
    q155: '{q154} = 3',
    q156: '{q154} = 4',
    q158: '{q157} = 3',
    q159: '{q157} = 4',
    q161: '{q160} = 3',
    q162: '{q160} = 4',
    q164: '{q163} = 3',
    q165: '{q163} = 4',
    q167: '{q166} = 3',
    q168: '{q166} = 4',
    q170: '{q169} = 3',
    q171: '{q169} = 4',
    q173: '{q172} = 3',
    q174: '{q172} = 4',
    q176: '{q175} = 3',
    q177: '{q175} = 4',
    q179: '{q178} = 3',
    q180: '{q178} = 4',
    q182: '{q181} = 3',
    q183: '{q181} = 4',
    q185: '{q184} = 3',
    q186: '{q184} = 4',
    q188: '{q187} = 3',
    q189: '{q187} = 4',
    q191: '{q190} = 3',
    q192: '{q190} = 4',
    q194: '{q193} = 3',
    q195: '{q193} = 4',
    // 7-1-21 其他服務：未填 → 跳過 B/B1/B2；B 題選項1=B1、選項2=B2
    q197: '{q196} notempty',
    q198: '({q196} notempty) and ({q197} = 1)',
    q199: '({q196} notempty) and ({q197} = 2)',
  })
  // 7-1-21（題號196）開放填答非必填；notRequired 無轉換工具，吃 0-based 原始索引
  const notRequired = [195]
  const mappedRateChoice = null
  const pageBreakQuestions = null
  const triggersConditionsInit = null
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  const endSurveyMsg = (survey) => {
    setCompletionMessage(
      `<div>感謝您的填答～歡迎轉發分享！<br><br>想參加更多問卷調查活動？手指點點就可以抽獎賺好康！<br>點擊下方按鈕加入 EZChoice APP<br>或複製下載連結 <a href="https://ezc-appweb.dsigroup.com.tw/app.html" target="_blank">https://ezc-appweb.dsigroup.com.tw/app.html</a><br>裡面還有更多填問卷拿購物金的活動唷～<br>各種好玩的問卷等你來玩！<br><br>想知道更多好康問卷、活動消息，請追蹤關注<br><a href="https://www.facebook.com/EasyCTRs" target="_blank">【輕鬆點點】粉絲專頁</a><br><br>加入我們的 <a href="https://lin.ee/HWETcqy" target="_blank">LINE@</a>，<br>隨時得到最新填問卷獲好禮的消息。<br><br><h4 style="text-align: center">請按「完成」前往下載 EZChoice 或填寫更多問卷</h4></div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving: requiresLogin.login || webAuthType > 0,
    webAuthType,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
